import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Video, Eye, EyeOff, Trash2, Radio, Play, Clock, Users, Edit2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";

export default function AdminStreams() {
  const [streams, setStreams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteStream, setDeleteStream] = useState(null);
  const [editStream, setEditStream] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [tab, setTab] = useState("streams");

  useEffect(() => { fetchData(); }, [tab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === "streams") {
        const res = await axios.get(`${API}/admin/streams`, { withCredentials: true });
        setStreams(res.data || []);
      } else {
        const res = await axios.get(`${API}/users/list`, { withCredentials: true });
        setUsers(res.data || []);
      }
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const saveTitle = async () => {
    if (!editStream) return;
    try {
      await axios.put(`${API}/streams/${editStream}/edit`, { title: editTitle }, { withCredentials: true });
      toast.success("Title updated");
      setEditStream(null);
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const toggleVisibility = async (streamId) => {
    try {
      const res = await axios.put(`${API}/streams/${streamId}/visibility`, {}, { withCredentials: true });
      toast.success(res.data.is_hidden ? "Stream hidden" : "Stream visible");
      fetchData();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async () => {
    if (!deleteStream) return;
    try {
      await axios.delete(`${API}/streams/${deleteStream}`, { withCredentials: true });
      toast.success("Stream deleted");
      setDeleteStream(null);
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleLivePermission = async (userId) => {
    try {
      const res = await axios.put(`${API}/users/${userId}/live-permission`, {}, { withCredentials: true });
      toast.success(res.data.can_go_live ? "Live permission granted" : "Live permission revoked");
      fetchData();
    } catch { toast.error("Failed to update"); }
  };

  return (
    <AdminLayout currentPage="Streams">
      <div className="space-y-6" data-testid="admin-streams-page">
        <div>
          <h1 className="text-2xl font-bold text-deep-blue">Live Streams & Permissions</h1>
          <p className="text-slate-500 mt-1">Manage streams and control who can go live</p>
        </div>

        <div className="flex gap-2">
          <button onClick={() => setTab("streams")} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "streams" ? 'bg-deep-blue text-white' : 'bg-white border text-slate-600'}`}>Streams</button>
          <button onClick={() => setTab("permissions")} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "permissions" ? 'bg-deep-blue text-white' : 'bg-white border text-slate-600'}`}>Live Permissions</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-deep-blue" /></div>
        ) : tab === "streams" ? (
          <div className="space-y-3">
            {streams.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border"><Video className="w-12 h-12 text-slate-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-slate-600">No streams yet</h3></div>
            ) : streams.map(s => (
              <div key={s.stream_id} className={`flex items-center gap-4 p-4 bg-white rounded-xl border ${s.is_hidden ? 'opacity-60' : ''}`} data-testid={`admin-stream-${s.stream_id}`}>
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                  {s.status === "live" ? <Radio className="w-5 h-5 text-red-500 animate-pulse" /> : s.has_recording ? <Play className="w-5 h-5 text-awareness-blue" /> : <Video className="w-5 h-5 text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-deep-blue truncate">{s.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                    <span>{s.broadcaster_name}</span>
                    <span className={`px-1.5 py-0.5 rounded font-semibold ${s.status === 'live' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{s.status?.toUpperCase()}</span>
                    {s.has_recording && <span className="text-awareness-blue">Has Recording</span>}
                    {s.is_hidden && <span className="text-amber-600">Hidden</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-400">{s.is_hidden ? "Hidden" : "Visible"}</span>
                    <Switch checked={!s.is_hidden} onCheckedChange={() => toggleVisibility(s.stream_id)} />
                  </div>
                  {s.has_recording && (
                    <a href={`${API}/streams/${s.stream_id}/recording`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm"><Play className="w-4 h-4" /></Button>
                    </a>
                  )}
                  <Button variant="outline" size="sm" onClick={() => { setEditStream(s.stream_id); setEditTitle(s.title); }}><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => setDeleteStream(s.stream_id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {users.map(u => (
              <div key={u.user_id} className="flex items-center gap-4 p-4 bg-white rounded-xl border" data-testid={`user-perm-${u.user_id}`}>
                <div className="w-10 h-10 rounded-full bg-deep-blue flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {u.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-deep-blue">{u.name}</h3>
                  <p className="text-xs text-slate-400">{u.email} — {u.role}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-slate-500">{u.can_go_live ? "Can Go Live" : "No Live Access"}</span>
                  <Switch checked={u.can_go_live || u.role === "super_admin" || u.role === "admin"} disabled={u.role === "super_admin" || u.role === "admin"} onCheckedChange={() => toggleLivePermission(u.user_id)} data-testid={`toggle-live-${u.user_id}`} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Title Dialog */}
        <Dialog open={!!editStream} onOpenChange={() => setEditStream(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle>Edit Stream Title</DialogTitle></DialogHeader>
            <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Stream title" data-testid="edit-stream-title" />
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => setEditStream(null)}>Cancel</Button>
              <Button onClick={saveTitle} className="bg-deep-blue text-white">Save</Button>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteStream} onOpenChange={() => setDeleteStream(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Stream</AlertDialogTitle>
              <AlertDialogDescription>This will permanently delete the stream and its recording. This cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
